import { useEffect, useMemo, useState } from 'react';

const SEARCH_HISTORY_STORAGE_KEY = 'motito.search.history.v1';
const MAX_RECENT_TERMS = 8;

interface SearchHistorySnapshot {
  terms: string[];
}

function normalizeTerm(value: string): string {
  return value.trim();
}

function hydrateSearchHistory(): SearchHistorySnapshot {
  if (typeof window === 'undefined') {
    return { terms: [] };
  }

  try {
    const raw = window.localStorage.getItem(SEARCH_HISTORY_STORAGE_KEY);
    if (!raw) {
      return { terms: [] };
    }

    const parsed = JSON.parse(raw) as {
      terms?: unknown;
    };

    const terms = Array.isArray(parsed.terms)
      ? parsed.terms.filter((term): term is string => typeof term === 'string')
      : [];

    return {
      terms: terms.slice(0, MAX_RECENT_TERMS),
    };
  } catch {
    return { terms: [] };
  }
}

export const useSearchHistory = () => {
  const [snapshot, setSnapshot] = useState<SearchHistorySnapshot>({ terms: [] });

  useEffect(() => {
    setSnapshot(hydrateSearchHistory());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(
        SEARCH_HISTORY_STORAGE_KEY,
        JSON.stringify({
          terms: snapshot.terms,
        })
      );
    } catch {
      // Ignore storage write issues (private mode/storage quota)
    }
  }, [snapshot]);

  const addTerm = (value: string) => {
    const term = normalizeTerm(value);
    if (!term) {
      return;
    }

    setSnapshot((current) => {
      const deduped = current.terms.filter(
        (item) => item.toLowerCase() !== term.toLowerCase()
      );
      return {
        ...current,
        terms: [term, ...deduped].slice(0, MAX_RECENT_TERMS),
      };
    });
  };

  return useMemo(
    () => ({
      terms: snapshot.terms,
      addTerm,
    }),
    [snapshot]
  );
};
